import { useCallback, useEffect, useState } from 'react';

import { useSessionAsync } from 'src/app/shared/hooks/session';
import { useSessionConfiguration } from 'src/app/shared/hooks/sessionConfiguration';
import { isLoaded } from 'src/app/shared/utils/async';

import { useBannerType } from './useBannerType';
import { useCookie } from './useCookie';
import { useCopy } from './useCopy';
import { useTracking } from './useTracking';

type Result = {
	isTermsOfServiceBannerDisplayed: boolean;
	dismissTermsOfServiceBanner: () => void;
	handleTermsOfServiceBannerView: () => void;
	trackTermsOfServiceLinkClick: () => void;
	termsOfServiceBannerCopy: Readonly<{ body: string }>;
};

export function useTermsOfServiceBanner(): Result {
	const session = useSessionAsync();

	const {
		featureConfigs: { tosLegalNoticeBanner: config },
	} = useSessionConfiguration();

	const bannerType = useBannerType(config);

	const { getCount, decreaseCount } = useCookie(bannerType, config);

	const { trackDismiss, trackBannerView, trackLinkClick } = useTracking(bannerType);

	const [displayBanner, setDisplayBanner] = useState(false);

	const isTermsOfServiceBannerDisplayed = !!bannerType && displayBanner;

	const dismissTermsOfServiceBanner = useCallback(() => {
		trackDismiss();
		decreaseCount(0);
		setDisplayBanner(false);
	}, [decreaseCount, trackDismiss]);

	const [viewHandled, setViewHandled] = useState(false);

	const handleTermsOfServiceBannerView = useCallback(() => {
		if (!isLoaded(session) || viewHandled) return;

		// do following only when session is available (for server-cached pages),
		// to make sure we have access to cookie prefs
		setViewHandled(true);
		trackBannerView();
		decreaseCount();
	}, [decreaseCount, trackBannerView, session, viewHandled]);

	useEffect(() => {
		setDisplayBanner(getCount() > 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // we only want to do this once at the start

	const termsOfServiceBannerCopy = useCopy(bannerType, config);

	return {
		isTermsOfServiceBannerDisplayed: !!termsOfServiceBannerCopy.body && isTermsOfServiceBannerDisplayed,
		dismissTermsOfServiceBanner,
		termsOfServiceBannerCopy,
		handleTermsOfServiceBannerView,
		trackTermsOfServiceLinkClick: trackLinkClick,
	};
}
